import React, { useEffect, useState } from 'react';
import {
  Table,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  TableHead,
  TableFooter,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { Iterable } from 'immutable';
import { useSelector } from 'react-redux';

import Cause from 'entities/cause/model';
import { selectOrganization } from 'entities/user/selectors';
import { PureRender } from 'utils/component-helpers';
import { getAppDomain } from 'utils/getAppDomain';
import { TableBox } from 'retired/Box';
import ExternalLinkIcon from 'assets/icons/external-link.svg';
import Img from 'retired/Img';

interface HeaderColumnInterface {
  id: string;
  label: string;
  sorted?: boolean;
  causes: Cause[];
}

interface Props {
  causes: Iterable<string, Cause>;
}

const CausesTable = (props: Props): JSX.Element => {
  const { causes } = props;
  const organization = useSelector(selectOrganization);

  const getSorted = (
    sortedBy: string,
    order: string,
    unSorted: Cause[] = causes ? causes.toArray() : []
  ): Cause[] => {
    if (sortedBy === 'type') {
      return unSorted.sort((a: Cause, b: Cause) => {
        if (!a.type || !b.type) {
          return 1;
        }
        return order === 'desc' ? a.type.localeCompare(b.type) : b.type.localeCompare(a.type);
      });
    } else if (sortedBy === 'name') {
      return unSorted.sort((a: Cause, b: Cause) => {
        if (!a.name || !b.name) {
          return 1;
        }
        return order === 'desc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      });
    } else {
      return unSorted.sort((a: Cause, b: Cause) => {
        const first = a[sortedBy] ? new Date(a[sortedBy]).getTime() : 0;
        const second = b[sortedBy] ? new Date(b[sortedBy]).getTime() : 0;
        return order === 'desc' ? first - second : second - first;
      });
    }
  };

  const columns: any[] = [
    { id: 'name', label: 'Name', sorted: true },
    { id: 'type', label: 'Type', sorted: true },
    { id: 'public', label: 'Public', sorted: true },
    { id: 'view', label: 'View in App' },
  ];

  const [values, setValues] = useState({
    sortedBy: 'type',
    order: 'asc',
    sortedCauses: getSorted('type', 'asc'),
  });

  const onSortedLabelClick = (item: HeaderColumnInterface): void => {
    if (item.id === values.sortedBy) {
      const order = values.order === 'desc' ? 'asc' : 'desc';
      setValues({
        sortedBy: item.id,
        order,
        sortedCauses: getSorted(item.id, order),
      });
    } else {
      setValues({
        sortedBy: item.id,
        order: 'desc',
        sortedCauses: getSorted(item.id, 'desc'),
      });
    }
  };

  useEffect(() => {
    setValues({
      sortedBy: 'type',
      order: 'desc',
      sortedCauses: getSorted('type', 'desc'),
    });
  }, [causes]);

  return (
    <TableBox>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.filter(Boolean).map((item: HeaderColumnInterface) => (
              <TableCell key={item.id} size="medium">
                {item.sorted ? (
                  <TableSortLabel
                    direction={values.order}
                    active={values.sortedBy === item.id}
                    onClick={(): void => {
                      onSortedLabelClick(item);
                    }}
                  >
                    {item.label}
                  </TableSortLabel>
                ) : (
                  item.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {causes?.size > 0 ? (
          <TableBody>
            {values.sortedCauses.map((cause: Cause) => (
              <TableRow key={cause.id}>
                <TableCell size="medium">
                  <Link to={`/causes/edit/${cause.id}`}>{cause.name}</Link>
                </TableCell>
                <TableCell size="medium">{cause.type}</TableCell>
                <TableCell size="medium">{String(cause.public)}</TableCell>
                <TableCell size="medium">
                  <a
                    href={`${getAppDomain([organization])}/causes/${cause?.type?.toLowerCase()}/${
                      cause.id
                    }`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Img
                      src={ExternalLinkIcon}
                      alt="External Link Icon"
                      style={{
                        width: 16,
                        height: 16,
                        marginLeft: 5,
                      }}
                    />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>
                You have no causes
              </TableCell>
            </TableRow>
          </TableBody>
        )}
        <TableFooter>
          <TableRow>
            <TableCell
              size="medium"
              colSpan={5}
              style={{
                color: '#0000008a',
                fontSize: '0.875rem',
              }}
            >
              Total: {values.sortedCauses.length}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableBox>
  );
};

export default PureRender(CausesTable);
